import React from 'react';
import { cn } from '@ideoz/ui';
import Logo from '../atoms/Logo';

/**
 * AuthLayoutTemplate Props
 *
 * Single-column, centered authentication layout: a branded card on a plain
 * background. Used by login / register / reset-password pages.
 */
export interface AuthLayoutTemplateProps {
  /** The authentication form rendered inside the card. */
  children: React.ReactNode;

  /** Optional card heading (e.g. "Welcome back"). */
  title?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Optional small footer under the card (e.g. legal / help links). */
  footer?: React.ReactNode;

  /** Extra classes for the card. */
  className?: string;

  /**
   * @deprecated The promotional right column was removed in favour of the
   * single-column card. Kept for call-site compatibility; no longer rendered.
   */
  rightContent?: React.ReactNode;
  /** @deprecated Kept for call-site compatibility; no longer affects layout. */
  isWeb?: boolean;
}

/**
 * AuthLayoutTemplate
 *
 * A simple, centered auth shell: the Ideoz wordmark above a rounded, elevated
 * card holding the form. Solid backgrounds, dark-mode aware.
 *
 * ```tsx
 * <AuthLayoutTemplate title="Welcome back" subtitle="Sign in to continue">
 *   <LoginTemplate {...props} />
 * </AuthLayoutTemplate>
 * ```
 */
export const AuthLayoutTemplate: React.FC<AuthLayoutTemplateProps> = ({
  children,
  title,
  subtitle,
  footer,
  className,
}) => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex w-full max-w-md flex-col items-center">
        {/* Brand */}
        <div className="mb-8 flex items-center gap-1.5">
          <Logo width={92} height={24} />
          {/* Product-stage tag beside the logo. */}
          <span
            className="inline-flex items-center rounded-sm bg-accent px-1.5 py-1 text-[10px] font-semibold uppercase leading-none tracking-wide text-accent-foreground"
            data-testid="sidebar-beta-badge"
          >
            Beta
          </span>
        </div>

        {/* Card */}
        <div
          className={cn(
            'w-full rounded-2xl border border-gray-200 bg-surface p-8 shadow-sm',
            'dark:border-white/10',
            className
          )}
        >
          {(title || subtitle) && (
            <header className="mb-6 text-center">
              {title && (
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
              )}
              {subtitle && (
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
              )}
            </header>
          )}
          {children}
        </div>

        {footer && (
          <div className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default AuthLayoutTemplate;
